import { useState } from 'react';
import type { AlignmentFlag, AlignmentSignal } from '../lib/api';
import type { RoleRecord } from '../lib/api';
import {
  alignmentSignalLabel,
  type ClusterMemberForDisplay,
  pickClusterSurvivor,
} from '../lib/alignmentSignalCopy';
import styles from './AlignmentReviewCard.module.css';

export interface AlignmentReviewCardProps {
  flag: AlignmentFlag;
  /** The user's full role list — populates the "Move to" picker. Every role
   *  is a real corpus role (NO INVENTION: nothing here is a generated
   *  option); the suggested role (flag.suggestedRole) is pre-selected when
   *  present, otherwise the picker opens on the first role in the list. */
  roles: RoleRecord[];
  /** True while THIS card's ruling is in flight — disables its own actions
   *  (a second tap can't race the first). Other cards stay interactive; the
   *  "one at a time" discipline lives in the parent (mirrors dragRehome's
   *  own single-pending-card note), not a global page-level lock. */
  saving: boolean;
  onMove: (roleId: string) => void;
  onKeep: () => void;
  onFlagSplit: () => void;
  /** RESUME-POLISH lane, part c: MERGE this card into `survivorId`. Only
   *  ever called when this card carries a 'near_duplicate' signal. */
  onMerge: (survivorId: string) => void;
}

/** Find this flag's 'near_duplicate' signal, if any. A card can carry at
 *  most one (the detector emits exactly one near_duplicate signal per
 *  achievement — see AlignmentReviewService.detectNearDuplicateClusters). */
function findNearDuplicateSignal(flag: AlignmentFlag): AlignmentSignal | undefined {
  return flag.signals.find((s) => s.kind === 'near_duplicate');
}

/** One flagged achievement — the headline, "filed under: X", evidence chips
 *  (the exact matched text, honest — never a paraphrase), the suggested home
 *  when present, and the terminal actions. One tap each; the parent owns the
 *  optimistic-UI + server-confirm + rollback discipline
 *  (useAlignmentReview.rule) — this component only reports the user's
 *  choice, exactly like BucketPicker's onChange contract.
 *
 * THE CLUSTER VARIANT (RESUME-POLISH lane, part c): when this card carries a
 * 'near_duplicate' signal, an additional panel renders ABOVE the normal
 * evidence row — every real headline in the cluster (this card's own +
 * signal.clusterMembers, verbatim, never a paraphrase), with the richest one
 * marked as the one that would survive, plus a MERGE action that merges
 * *this* card into that survivor. The normal actions (Move / Keep / Flag to
 * split) remain available underneath — a near-duplicate card is STILL a
 * normal flagged card in every other respect (it can, for instance, ALSO be
 * misfiled under the wrong role, stacking a cross_employer_leakage signal
 * alongside near_duplicate — MERGE is additive, not exclusive). */
export function AlignmentReviewCard({
  flag,
  roles,
  saving,
  onMove,
  onKeep,
  onFlagSplit,
  onMerge,
}: AlignmentReviewCardProps) {
  const [selectedRoleId, setSelectedRoleId] = useState(
    flag.suggestedRole?.id ?? roles[0]?.id ?? '',
  );

  const filedUnder = flag.currentRole
    ? `${flag.currentRole.company} — ${flag.currentRole.title}`
    : 'Unattached';

  function handleMove() {
    if (!selectedRoleId || saving) return;
    onMove(selectedRoleId);
  }

  const nearDuplicateSignal = findNearDuplicateSignal(flag);
  // Every real member of the cluster, THIS card's own entry first (so a full
  // richness tie deterministically favours the card currently rendering,
  // matching pickClusterSurvivor's own first-seen tiebreak).
  const clusterMembers: ClusterMemberForDisplay[] = nearDuplicateSignal
    ? [
        { achievementId: flag.achievementId, headline: flag.headline },
        ...(nearDuplicateSignal.clusterMembers ?? []),
      ]
    : [];
  const survivor = clusterMembers.length ? pickClusterSurvivor(clusterMembers) : null;
  const isSurvivorThisCard = survivor?.achievementId === flag.achievementId;

  function handleMerge() {
    if (saving || !survivor || isSurvivorThisCard) return;
    onMerge(survivor.achievementId);
  }

  return (
    <div className={styles.card}>
      <div className={styles.headline}>{flag.headline}</div>
      <div className={styles.filedUnder}>
        Filed under: <span className={styles.filedUnderValue}>{filedUnder}</span>
      </div>

      {nearDuplicateSignal ? (
        <div className={styles.clusterPanel} data-testid="cluster-panel">
          <div className={styles.clusterHeading}>
            Meira noticed these {clusterMembers.length} read like the same story told different
            ways
          </div>
          <ul className={styles.clusterList}>
            {clusterMembers.map((m) => (
              <li
                key={m.achievementId}
                className={
                  m.achievementId === survivor?.achievementId
                    ? `${styles.clusterItem} ${styles.clusterItemSurvivor}`
                    : styles.clusterItem
                }
              >
                <span className={styles.clusterItemText}>{m.headline}</span>
                {m.achievementId === survivor?.achievementId ? (
                  <span className={styles.clusterSurvivorBadge}>keeps this one</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.evidenceRow}>
        {flag.signals.map((signal, i) => (
          <span
            key={`${signal.kind}-${i}`}
            className={styles.evidenceChip}
            title={alignmentSignalLabel(signal.kind)}
          >
            {signal.kind === 'near_duplicate' ? alignmentSignalLabel(signal.kind) : signal.evidence}
          </span>
        ))}
      </div>

      {flag.suggestedRole ? (
        <div className={styles.suggestion}>
          Meira's guess:{' '}
          <span className={styles.suggestionValue}>
            {flag.suggestedRole.company} — {flag.suggestedRole.title}
          </span>
        </div>
      ) : null}

      <div className={styles.actions}>
        <select
          className={styles.rolePicker}
          value={selectedRoleId}
          disabled={saving || roles.length === 0}
          onChange={(e) => setSelectedRoleId(e.target.value)}
          aria-label="Choose the role to move this story to"
        >
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.company} — {r.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.moveButton}`}
          onClick={handleMove}
          disabled={saving || !selectedRoleId}
        >
          {saving ? <span className={styles.spinner} aria-hidden="true" /> : null}
          Move
        </button>
        {nearDuplicateSignal && !isSurvivorThisCard ? (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.mergeButton}`}
            onClick={handleMerge}
            disabled={saving}
          >
            {saving ? <span className={styles.spinner} aria-hidden="true" /> : null}
            Merge into the richest version
          </button>
        ) : null}
        <button
          type="button"
          className={styles.actionButton}
          onClick={onKeep}
          disabled={saving}
        >
          {nearDuplicateSignal ? 'Keep all — these are different' : "It's right where it is"}
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={onFlagSplit}
          disabled={saving}
        >
          Flag to split later
        </button>
      </div>
    </div>
  );
}
